import { FC, useEffect, useMemo, useState } from 'react';
import {
  AdvancedPagination,
  Button,
  Card,
  Divider,
  InputSelect,
  RadixTable,
  TColumn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DailyTradeChart,
  DailyBalanceChart,
} from '@features/ui';
import { useProducts } from '@hooks/useProducts';
import {
  accountStatusOptionsSimple,
  payoutStatusOptions,
} from '@features/admin/reports/reportData';
import {
  TPAActivePayoutsAddonsApiRow,
  TPAActivePayoutsAddonsData,
} from '@src/types/reports';
import { StackedData, BalanceStackedData } from '@src/types';
import { BarChart2 } from 'lucide-react';
import dayjs from 'dayjs';

import {
  exportPAActivePayoutsAddons,
  getPAActivePayoutsAddons,
} from '@src/services/reports';
import {
  getDailyProfitLossAndBalance,
  getDailyTradeVolumesAndContracts,
} from '@src/services/payout';
import { downloadFile } from '@src/lib/utils';

const formatMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';

  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const DEFAULT_PAGE_SIZE = 25;

const formatDateLabel = (value: string | null | undefined): string => {
  if (!value) return '-';

  return dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '-';
};

const formatDateTimeLabel = (value: string | null | undefined): string => {
  if (!value) return '-';

  return dayjs(value).isValid()
    ? dayjs(value).format('DD MMMM YYYY, HH:mm:ss')
    : '-';
};

const getProductIdParam = (selectedProduct: string): number | undefined => {
  if (!selectedProduct || selectedProduct === 'show_all') {
    return undefined;
  }

  return Number(selectedProduct);
};

const getAutoLiquidatedMessage = (
  row: TPAActivePayoutsAddonsData | null
): string | null => {
  if (!row) {
    return null;
  }

  const trimmedNotes = row.notes?.trim();

  if (trimmedNotes) {
    return trimmedNotes;
  }

  if (row.auto_liquidated_on) {
    return `Auto-liquidated on ${formatDateTimeLabel(row.auto_liquidated_on)}`;
  }

  return null;
};

const mapApiRowToTableRow = (
  row: TPAActivePayoutsAddonsApiRow
): TPAActivePayoutsAddonsData => {
  const validFrom = formatDateLabel(row.valid_from);
  const validTo = formatDateLabel(row.valid_to);

  return {
    id: row.id,
    user_id: row.user_id,
    email: row.email,
    name: row.name,
    account_id: row.account_id ?? '-',
    account_package: row.package ?? '-',
    eval_purchase: formatDateLabel(row.eval_purchase_date),
    valid: `${row.trading_days ?? 0} / ${validFrom} - ${validTo}`,
    status: row.status ?? undefined,
    auto_liquidated_on: row.auto_liquidated_on,
    notes: row.notes,
    trading_days: row.trading_days ?? 0,
    balance: formatMoney(row.balance),
    net_pl: formatMoney(row.net_pnl),
    acc_payouts: `${row.payouts_count ?? 0}`,
    total_paid_by_customer: formatMoney(row.total_paid),
    acc_payout_amount: formatMoney(row.payouts_amount),
    addon_reduced_days_to_qualify: Boolean(row.eval_quality_x_days),
    addon_extra_days_200: Boolean(row.pa_extra_amount),
    addon_30_day_payouts: Boolean(row.pa_payout_x_days_10),
    addon_15_day_payouts: Boolean(row.pa_payout_x_days_15),
    addon_lifetime_days_179: Boolean(row.performance_fee_lifetime_one_time),
    addon_lifetime_days_365: Boolean(row.performance_fee_lifetime_pay_upfront),
    addon_performance_fee_monthly: Boolean(row.performance_fee_monthly),
    addon_performance_fee_lifetime_one_time: Boolean(
      row.performance_fee_lifetime_one_time
    ),
    addon_performance_fee_lifetime_pay_upfront: Boolean(
      row.performance_fee_lifetime_pay_upfront
    ),
    addon_performance_fee_lifetime_pay_partial: Boolean(
      row.performance_fee_lifetime_pay_partial
    ),
  };
};

export const PAActivePaypoutsAddons: FC = () => {
  const { products } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState<string>('show_all');
  const [accountStatus, setAccountStatus] = useState<string>('show_all');
  const [payoutStatus, setPayoutStatus] = useState<string>('show_all');
  const [rows, setRows] = useState<TPAActivePayoutsAddonsData[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<TPAActivePayoutsAddonsData | null>(null);
  const [tradeData, setTradeData] = useState<StackedData[]>([]);
  const [balanceData, setBalanceData] = useState<BalanceStackedData[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    const accountId = selectedAccount?.id;

    if (!statsDialogOpen || !accountId) {
      setTradeData([]);
      setBalanceData([]);

      return;
    }

    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        const [tradeRes, balanceRes] = await Promise.all([
          getDailyTradeVolumesAndContracts(accountId),
          getDailyProfitLossAndBalance(accountId),
        ]);

        setTradeData(tradeRes.data);
        setBalanceData(balanceRes.data);
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
        setTradeData([]);
        setBalanceData([]);
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [statsDialogOpen, selectedAccount?.id]);

  const productOptions = useMemo(
    () => [{ label: 'Showall', value: 'show_all' }, ...products],
    [products]
  );

  const fetchReport = async (page = 1, nextPageSize = pageSize) => {
    try {
      setReportLoading(true);
      const response = await getPAActivePayoutsAddons({
        product_id: getProductIdParam(selectedProduct),
        account_status: accountStatus as 'show_all' | 'active_only',
        payout_status: payoutStatus as 'show_all' | 'with_payout_only',
        page,
        page_size: nextPageSize,
      });

      setRows(response.data.map(mapApiRowToTableRow));
      setTotalCount(response.total_count ?? 0);
      setCurrentPage(response.current_page ?? page);
      setPageSize(response.page_size ?? nextPageSize);
      setTotalPages(response.total_pages ?? 1);
    } catch (error) {
      console.error('Failed to fetch PA active payouts addons report:', error);
      setRows([]);
      setTotalCount(0);
      setCurrentPage(1);
      setTotalPages(1);
    } finally {
      setReportLoading(false);
    }
  };

  const columns = useMemo(
    (): TColumn<TPAActivePayoutsAddonsData>[] => [
      {
        title: 'Name',
        render: (item) => <p className="font-medium">{item?.name ?? '-'}</p>,
      },
      {
        title: 'Account ID',
        render: (item) => (
          <div className="flex flex-col gap-1">
            <p className="text-xs">{item?.account_id ?? '-'}</p>
            <p className="text-[10px] text-gray-400 max-w-[200px]">
              {item?.account_package ?? ''}
            </p>
          </div>
        ),
      },
      {
        title: 'Eval Purchase',
        render: (item) => <p>{item?.eval_purchase ?? '-'}</p>,
      },
      {
        title: 'Valid',
        render: (item) => {
          if (!item?.valid || item.valid === '-') return <p>-</p>;

          const parts = item.valid.split(' - ');
          const startPart = parts[0]?.split(' / ')[1]?.trim();
          const endPart = parts[1]?.trim();

          return (
            <div className="flex flex-col">
              <p className="text-xs">
                <sup className="text-[8px] mr-0.5">S</sup>
                {startPart ?? ''}
              </p>
              <p className="text-xs">
                <sup className="text-[8px] mr-0.5">E</sup>
                {endPart ?? ''}
              </p>
            </div>
          );
        },
      },
      {
        title: 'Trading Days',
        render: (item) => (
          <div className="flex items-center justify-center">
            <span
              className={`px-2 py-1 rounded text-xs ${
                (item?.trading_days ?? 0) >= 5
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}
            >
              {item?.trading_days ?? 0}
            </span>
          </div>
        ),
      },
      {
        title: 'Balance',
        render: (item) => <p>{item?.balance ?? '-'}</p>,
      },
      {
        title: 'Net P/L',
        render: (item) => (
          <p
            className={
              item?.net_pl?.includes('-') ? 'text-red-400' : 'text-green-400'
            }
          >
            {item?.net_pl ?? '-'}
          </p>
        ),
      },
      {
        title: 'Acc. Payouts',
        render: (item) => <p>{item?.acc_payouts ?? '-'}</p>,
      },
      {
        title: 'Total paid by customer',
        render: (item) => <p>{item?.total_paid_by_customer ?? '-'}</p>,
      },
      {
        title: 'Acc. Payout $',
        render: (item) => <p>{item?.acc_payout_amount ?? '-'}</p>,
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">
              Reduced Days to qualify
            </span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_reduced_days_to_qualify ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Extra $500</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_extra_days_200 ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">10 day payouts</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_30_day_payouts ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">15 day payouts</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_15_day_payouts ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Lifetime $119</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_lifetime_days_179 ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Lifetime $90</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_lifetime_days_365 ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Performance Fee Monthly</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_performance_fee_monthly ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Performance Fee Lifetime One Time</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_performance_fee_lifetime_one_time ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Performance Fee Lifetime Upfront</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_performance_fee_lifetime_pay_upfront ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="flex flex-col items-center">
            <span>Addon</span>
            <span className="text-[10px] font-normal">Performance Fee Lifetime Partial</span>
          </div>
        ),
        render: (item) => (
          <div className="flex justify-center">
            {item?.addon_performance_fee_lifetime_pay_partial ? (
              <span className="text-green-400 text-lg">✓</span>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </div>
        ),
      },
      {
        title: '',
        render: (item) => (
          <Button
            className="gap-2"
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedAccount(item ?? null);
              setStatsDialogOpen(true);
            }}
          >
            <BarChart2 className="h-4 w-4" />
            Stats
          </Button>
        ),
      },
    ],
    []
  );

  const handleGetData = async () => {
    await fetchReport(1, pageSize);
  };

  const handlePageChange = async (page: number) => {
    await fetchReport(page, pageSize);
  };

  const handlePageSizeChange = async (nextPageSize: number) => {
    await fetchReport(1, nextPageSize);
  };

  const handleExportToExcel = async () => {
    try {
      setExportLoading(true);
      const response = await exportPAActivePayoutsAddons({
        product_id: getProductIdParam(selectedProduct),
        account_status: accountStatus as 'show_all' | 'active_only',
        payout_status: payoutStatus as 'show_all' | 'with_payout_only',
      });

      downloadFile(response.data, 'pa-active-account-with-payout-addons');
    } catch (error) {
      console.error('Failed to export PA active payouts addons report:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const selectedProductLabel = useMemo(() => {
    if (selectedProduct === 'show_all') {
      return 'All Packages';
    }

    return (
      productOptions.find((product) => product.value === selectedProduct)?.label ??
      'Selected Package'
    );
  }, [productOptions, selectedProduct]);

  return (
    <div>
      <div className="flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col mb-6">
        <div className="text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3">
          <h1 className="text-[23px]">Performance Accounts</h1>
        </div>
        <Button
          disabled={exportLoading || reportLoading || rows.length === 0}
          size="sm"
          variant="lime"
          onClick={handleExportToExcel}
        >
          {exportLoading ? 'EXPORTING...' : 'EXPORT TO EXCEL'}
        </Button>
      </div>

      <Card className="p-6">
        <div className="flex flex-wrap gap-6 mb-6">
          <div className="w-full flex gap-6 flex-wrap items-end">
            <div>
              <span>Account Package</span>
              <InputSelect
                className="min-w-[240px]"
                options={productOptions}
                value={selectedProduct}
                onChange={(value) => setSelectedProduct(value as string)}
              />
            </div>
            <div>
              <span>Account Status</span>

              <InputSelect
                className="min-w-[240px]"
                options={accountStatusOptionsSimple}
                value={accountStatus}
                onChange={(value) => setAccountStatus(value as string)}
              />
            </div>

            <div>
              <span>Payout Status</span>
              <InputSelect
                className="min-w-[240px]"
                options={payoutStatusOptions}
                value={payoutStatus}
                onChange={(value) => setPayoutStatus(value as string)}
              />
            </div>

            <Button
              className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
              disabled={reportLoading}
              onClick={handleGetData}
            >
              {reportLoading ? 'Loading...' : 'Run Report'}
            </Button>
          </div>
        </div>
        <div className="text-lg">
          Showing {selectedProductLabel}
        </div>
        <div className="mb-4">{totalCount} accounts</div>
        <Divider />
        <RadixTable columns={columns} data={rows} />
        {totalCount > 0 ? (
          <AdvancedPagination
            className="mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[25, 50, 100, 250]}
          />
        ) : null}
      </Card>

      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-[980px] w-[98%]" size="xl">
          <DialogHeader>
            <DialogTitle>
              Account {selectedAccount?.account_id ?? ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Header Info Row */}
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-gray-400">Active Trading Days: </span>
                <span className="bg-red-500 text-white px-2 py-0.5 rounded">
                  {selectedAccount?.trading_days ?? 0}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Status: </span>
                <span className="bg-red-500 text-white px-2 py-0.5 rounded uppercase">
                  {selectedAccount?.status ?? '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Balance: </span>
                <span className="font-semibold">
                  {selectedAccount?.balance ?? '-'}
                </span>
              </div>
            </div>

            {/* Alert Banner */}
            {getAutoLiquidatedMessage(selectedAccount) ? (
              <div className="bg-red-100 text-red-800 p-3 rounded text-sm">
                {getAutoLiquidatedMessage(selectedAccount)}
              </div>
            ) : null}

            {/* Daily Trade Volume vs Contracts Chart */}
            <div>
              <p className="text-center font-semibold mb-2">
                Daily Trade Volume vs Contracts
              </p>
              {chartLoading ? (
                <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              ) : (
                <DailyTradeChart data={tradeData} height={320} />
              )}
            </div>

            {/* Daily Net Profit / Loss vs Account Balance Chart */}
            <div>
              <p className="text-center font-semibold mb-2">
                Daily Net Profit / Loss vs Account Balance
              </p>
              {chartLoading ? (
                <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              ) : (
                <DailyBalanceChart data={balanceData} height={320} />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
